import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';
import axios from 'axios';
import {
  Palette, Bot, LayoutDashboard, Megaphone, Image, BarChart3,
  Trophy, MessageSquarePlus, ChevronLeft, ChevronRight, LogOut,
  Sun, Moon, Coins, MessageCircle, Bell, ChevronUp, X, FileImage,
  Mic, FolderHeart, Share2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Notification Bell (moved from App.js inline) ─── */
const NotificationBell = ({ sessionToken, isCollapsed }) => {
  const { colorTheme } = useColorTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const response = await axios.get(`${API}/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (e) { /* silent */ }
  }, [sessionToken]);

  useEffect(() => {
    if (sessionToken) {
      fetchNotifications();
      const id = setInterval(fetchNotifications, 10000);
      return () => clearInterval(id);
    }
  }, [sessionToken, fetchNotifications]);

  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${sessionToken}` } });
      setNotifications(p => p.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(p => Math.max(0, p - 1));
    } catch (e) { /* silent */ }
  };

  const getTimeAgo = (d) => {
    const s = Math.floor((Date.now() - new Date(d)) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const accent = colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-emerald-600 dark:text-emerald-400';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative w-full flex items-center ${isCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all`}
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 flex-shrink-0" />
        {!isCollapsed && <span className="text-sm font-medium">Notifications</span>}
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute bottom-full left-0 mb-2 w-80 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 z-[9999] max-h-96 overflow-hidden flex flex-col backdrop-blur-xl">
          <div className={`p-4 border-b border-slate-200/50 dark:border-slate-700/50`}>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-800 dark:text-white flex items-center gap-2">
                <Bell className="w-4 h-4" /> Notifications
              </h3>
              {unreadCount > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadCount} new</span>}
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-400 dark:text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => { if (!n.read) markAsRead(n.id); setShowDropdown(false); }}
                    className={`w-full p-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${!n.read ? 'bg-slate-50/80 dark:bg-slate-700/30' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${n.type === 'campaign_complete' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : n.type === 'campaign_failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
                        {n.type === 'campaign_complete' ? '✓' : n.type === 'campaign_failed' ? '✕' : '●'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-xs text-slate-800 dark:text-white truncate">{n.title}</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">{n.message}</p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{getTimeAgo(n.created_at)}</p>
                      </div>
                      {!n.read && <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-1.5" />}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {notifications.length > 0 && (
            <div className="p-2 border-t border-slate-200/50 dark:border-slate-700/50">
              <button onClick={() => setShowDropdown(false)} className={`w-full text-center text-xs font-medium ${accent} py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors`}>
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Main Sidebar ─── */
const SidebarV2 = ({ currentView, onNavigate, user, onLogout, onToggleCollapse, isCollapsed: collapsed, sessionToken, isMobile, showMobileMenu, setShowMobileMenu, isDark, toggleTheme }) => {
  const { currentColors, colorTheme } = useColorTheme();
  const [isCollapsed, setIsCollapsed] = useState(collapsed || false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  // Feature flag: admin-controlled visibility of the Poster Builder tab.
  // Admin users always see it (so they can review/test). Non-admins only see
  // it when admin has explicitly enabled it via /admin/system-settings/poster-builder.
  const [posterBuilderEnabled, setPosterBuilderEnabled] = useState(false);

  useEffect(() => { setIsCollapsed(collapsed); }, [collapsed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/system-settings/poster-builder`);
        if (!cancelled) setPosterBuilderEnabled(!!resp.data?.poster_builder_enabled);
      } catch {
        if (!cancelled) setPosterBuilderEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    onToggleCollapse(next);
  };

  const handleNav = (view) => {
    onNavigate(view);
    if (isMobile && setShowMobileMenu) setShowMobileMenu(false);
  };

  const menuItems = [
    { id: 'content-studio', icon: Palette, label: 'Content Studio' },
    { id: 'ai-assistant', icon: Bot, label: 'AI Assistant', badge: 'AI' },
    { id: 'campaigns', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'main', icon: Megaphone, label: 'Ad Creator Hub' },
    // Poster Builder is admin-gated via the public feature flag. Admins always
    // see it (with a faded eye-off icon while it's hidden for users), others
    // only see it when the flag is on.
    ...((posterBuilderEnabled || user?.is_admin)
      ? [{
          id: 'poster-builder',
          icon: FileImage,
          label: 'Poster Builder',
          badge: !posterBuilderEnabled && user?.is_admin ? 'BETA' : 'NEW',
        }]
      : []),
    { id: 'my-gallery', icon: Image, label: 'My Gallery' },
    { id: 'asset-library', icon: FolderHeart, label: 'Asset Library' },
    { id: 'voice-library', icon: Mic, label: 'Voice Library' },
    { id: 'public-reel', icon: Share2, label: 'Public Reel' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
    { id: 'achievements', icon: Trophy, label: 'Achievements' },
    { id: 'feedback', icon: MessageSquarePlus, label: 'Feedback' },
  ];

  // Theme-aware active item styling
  const activeClasses = colorTheme === 'purple'
    ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 ring-1 ring-purple-200 dark:ring-purple-800'
    : colorTheme === 'blue'
    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-800'
    : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-800';

  const activeDot = colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-emerald-500';
  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500';
  const badgeBg = colorTheme === 'purple' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : colorTheme === 'blue' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';

  const sidebarWidth = isCollapsed && !isMobile ? 'w-20' : 'w-72';

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && showMobileMenu && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 md:hidden" onClick={() => setShowMobileMenu(false)} />
      )}

      <div
        className={`
          ${isMobile
            ? `fixed inset-y-0 left-0 z-40 w-72 transform transition-transform duration-300 ${showMobileMenu ? 'translate-x-0' : '-translate-x-full'}`
            : `${sidebarWidth} fixed left-0 top-0 z-40`
          }
          backdrop-blur-2xl bg-white/80 dark:bg-slate-950/80 border-r border-slate-200/50 dark:border-slate-800/50 transition-all duration-300 ease-in-out flex flex-col h-screen overflow-hidden
        `}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="px-4 py-5 border-b border-slate-200/50 dark:border-slate-800/50 flex items-center justify-between flex-shrink-0">
          {(!isCollapsed || isMobile) && (
            <div className="flex items-center gap-2">
              <div className={`text-xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent tracking-tight`}>
                AdElevate
              </div>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${badgeBg}`}>
                Beta
              </span>
            </div>
          )}
          {!isMobile && (
            <button
              onClick={handleToggleCollapse}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500 transition-colors"
              title={isCollapsed ? 'Expand' : 'Collapse'}
              data-testid="sidebar-collapse-btn"
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          )}
          {isMobile && (
            <button onClick={() => setShowMobileMenu(false)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400" data-testid="sidebar-close-btn">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-hide" data-testid="sidebar-nav">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={`w-full flex items-center ${isCollapsed && !isMobile ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative ${
                    isActive
                      ? `${activeClasses} font-medium shadow-sm`
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100'
                  }`}
                  title={isCollapsed && !isMobile ? item.label : undefined}
                  data-testid={`sidebar-nav-${item.id}`}
                >
                  {/* Active indicator dot */}
                  {isActive && !isCollapsed && (
                    <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 ${activeDot} rounded-r-full`} />
                  )}
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {(!isCollapsed || isMobile) && (
                    <>
                      <span className="text-sm">{item.label}</span>
                      {item.badge && (
                        <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-md ${badgeBg}`}>
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Bottom Section */}
        <div className="flex-shrink-0 border-t border-slate-200/50 dark:border-slate-800/50 p-3 space-y-1">
          {/* Notification Bell */}
          <NotificationBell sessionToken={sessionToken} isCollapsed={isCollapsed && !isMobile} />

          {/* Profile & Settings */}
          <div className="relative">
            <button
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              className={`w-full flex items-center ${isCollapsed && !isMobile ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${showSettingsDropdown ? 'bg-slate-100 dark:bg-slate-800' : ''}`}
              data-testid="sidebar-profile-btn"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              {(!isCollapsed || isMobile) && (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{user?.name || 'User'}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{user?.email || ''}</p>
                  </div>
                  <ChevronUp className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${showSettingsDropdown ? '' : 'rotate-180'}`} />
                </>
              )}
            </button>

            {/* Settings Dropdown */}
            {showSettingsDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowSettingsDropdown(false)} />
                <div className={`absolute ${isCollapsed && !isMobile ? 'left-full ml-2 bottom-0' : 'bottom-full left-0 right-0 mb-2'} bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 z-50 overflow-hidden backdrop-blur-xl ${isCollapsed && !isMobile ? 'w-52' : ''}`}
                  data-testid="sidebar-settings-dropdown"
                >
                  <div className="p-1.5">
                    {/* Theme Toggle */}
                    <button
                      onClick={() => { toggleTheme(); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                      data-testid="sidebar-theme-toggle"
                    >
                      {isDark ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-slate-500" />}
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    {/* Credits */}
                    <button
                      onClick={() => { setShowSettingsDropdown(false); handleNav('credits'); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                      data-testid="sidebar-credits-btn"
                    >
                      <Coins className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Credits & Billing</span>
                    </button>
                    {/* WhatsApp */}
                    <button
                      onClick={() => { setShowSettingsDropdown(false); handleNav('whatsapp'); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                      data-testid="sidebar-whatsapp-btn"
                    >
                      <MessageCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">WhatsApp</span>
                    </button>
                    <div className="border-t border-slate-200/50 dark:border-slate-700/50 my-1" />
                    {/* Sign Out */}
                    <button
                      onClick={() => { setShowSettingsDropdown(false); onLogout(); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400"
                      data-testid="sidebar-logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SidebarV2;
